import { Activity, Siren, Gauge, BedDouble, CalendarDays, ClipboardPen, Stethoscope } from 'lucide-react';
import { benTaubOr, BB_DOC, ext } from './benTaubOr';

/*
 * Ben Taub PACU rotation. Built from the Blackboard PACU Orientation (orientation guide
 * and discharge scoring guide). See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const PACU = BB_DOC('_255046_1');

export const benTaubPacu = {
  accent: benTaubOr.accent,
  icon: Activity,
  tagline: 'Recovery, floor codes and airways, ICU rounds',
  notesKey: 'notes:ben-taub:pacu',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'codes', title: 'Codes & Airways', subtitle: 'Who to call, what to bring, how to chart', icon: Siren, action: 'guide:codes', featured: true },
      { id: 'scoring', title: 'Discharge Scoring', subtitle: 'Modified Aldrete and PADSS', icon: Gauge, action: 'guide:scoring' },
      { id: 'patients', title: 'PACU Patients', subtitle: 'Arrival, orders, sign-out, GI', icon: BedDouble, action: 'guide:patients' },
      { id: 'icu', title: 'ICU Rounds & Consents', subtitle: 'POD 1 follow-ups and add-on consents', icon: Stethoscope, action: 'guide:icu' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Pagers, huddle, handoff', icon: CalendarDays, action: 'guide:day' },
      { id: 'epic', title: 'Epic', subtitle: 'Out-of-OR intubation encounter', icon: ClipboardPen, action: 'guide:epic' },
    ] },
  ],

  contacts: {
    storageKey: 'ben-taub-pacu-favorites',
    groups: [
      { id: 'pacu', title: 'PACU and regional', open: true, items: [
        { label: 'PACU resident', phone: ext('9593'), note: 'Carries the Anesthesia and Code 1 pagers' },
        { label: 'Regional resident', phone: ext('9595'), note: 'First-case blocks' },
        { label: 'Regional faculty', phone: ext('9590') },
        { label: 'OR charge nurse', phone: ext('7250'), note: 'ICU add-on list' },
      ] },
      ...benTaubOr.contacts.groups,
    ],
  },

  locations: {
    ...benTaubOr.locations,
    groups: [
      { id: 'pacu', title: 'PACU day', items: [
        { name: 'Morning huddle', floor: '4th floor', where: 'Whiteboard outside OR 8', note: '06:35' },
        { name: 'Airway drug pack', floor: '4th floor OR pharmacy', note: 'Pick up at the start of the shift. Also in the 2nd floor PACU Pyxis' },
        { name: 'Airway tower', floor: '4th floor', where: 'Across from OR 10', note: 'The airway tech brings it to codes' },
      ] },
      ...benTaubOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'ben-taub-pacu-pinned-docs',
    groups: [
      { id: 'pacu', title: 'PACU', items: [
        { title: 'PACU orientation', subtitle: 'Orientation guide and discharge scoring. Blackboard', url: PACU },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    codes: {
      title: 'Codes & Airways',
      sections: [
        { title: 'You go to every one', blocks: [{ type: 'bullets', items: [
          'Code 1 trauma activations in the Emergency Center, floor codes, airway calls',
          'Tell the CON right away',
          'Call the airway anesthesia tech for the airway tower',
          'Carry the emergency drug pack. Bring extra paralytic',
          'Stat case: go set up the stat OR',
        ] }] },
        { title: 'After a floor intubation', blocks: [{ type: 'steps', items: [
          'Take two patient stickers: one for pharmacy to replace drugs, one to find the patient for charting',
          'Out-of-OR Intubation encounter in Epic, attending cosigns. See Epic',
          'Restock the airway pack at the 4th floor OR pharmacy or the 2nd floor PACU Pyxis',
        ] }] },
        { title: 'Evaluate at once', blocks: [{ type: 'bullets', items: [
          'Respiratory distress, chest pain, severe tachycardia, hypotension, bleeding',
        ] }] },
      ],
      sources: [{ title: 'PACU orientation guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },

    scoring: {
      title: 'Discharge Scoring',
      intro: 'Two points each. Nine of ten to leave.',
      segments: [
        { id: 'aldrete', label: 'Modified Aldrete', sections: [
          { title: 'Phase 1', blocks: [{ type: 'table', cols: ['', '2', '1', '0'], rows: [
            ['Activity', 'Moves all four limbs', 'Moves two', 'None'],
            ['Breathing', 'Deep breath and cough', 'Dyspneic or shallow', 'Apneic'],
            ['Blood pressure', 'Within 20 mmHg of baseline', '20–50 off', 'Over 50 off'],
            ['Consciousness', 'Fully awake', 'Rouses to voice', 'Unresponsive'],
            ['Saturation', 'Over 90% on room air', 'Over 90% with oxygen', 'Under 90% with oxygen'],
          ] }] },
        ] },
        { id: 'padss', label: 'PADSS', sections: [
          { title: 'Going home', blocks: [{ type: 'table', cols: ['', '2', '1', '0'], rows: [
            ['Vital signs', 'Within 20% of baseline', '20–40%', 'Over 40%'],
            ['Activity', 'Oriented and steady gait', 'One of the two', 'Neither'],
            ['Pain, nausea', 'Minimal', 'Moderate, treated', 'Severe'],
            ['Intake, output', 'Drinking and voided', 'One of the two', 'Neither'],
            ['Bleeding', 'No dressing change', 'Up to two changes', 'Three or more'],
          ] }] },
        ] },
      ],
      sources: [{ title: 'PACU discharge scoring guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },

    patients: {
      title: 'PACU Patients',
      sections: [
        { title: 'On arrival', blocks: [{ type: 'bullets', items: [
          'Assess the patient. Quick history and intraoperative events from the OR team',
          'PACU orders if the OR team has not placed them. Pain and nausea medications as needed',
        ] }] },
        { title: 'Sign-out', blocks: [{ type: 'bullets', items: [
          'Assess before discharge to the floor or home. Transfer and discharge orders come from the primary service',
          'Post Procedure Evaluation note at sign-out. The attending of record cosigns; if gone, another attending or the CON',
          'GI patients are signed out by faculty. Their pain and nausea are still yours',
        ] }] },
        { title: 'Overnight patients', blocks: [{ type: 'bullets', items: [
          'Get sign-out at 06:30 on anyone still in the PACU',
          'At 15:30, walk the PACU with the on-call senior and flag open issues. Some attendings want the same walk-through',
        ] }] },
      ],
      sources: [{ title: 'PACU orientation guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },

    icu: {
      title: 'ICU Rounds & Consents',
      sections: [
        { title: 'POD 1 rounds', blocks: [{ type: 'steps', items: [
          'After the regional check and the stat OR, round on every POD 1 patient on the Epic ICU list, unit by unit',
          'Follow-Up tab, Ane Post Anesthesia Evaluation template, quick note',
          'Follow-up Complete removes the patient from the list. Close the encounter',
        ] }] },
        { title: 'Add-on consents', blocks: [{ type: 'bullets', items: [
          'Call the OR charge nurse for the ICU add-on list',
          'Consent the ICU patients early, before the PACU fills. No preop evaluation needed',
          'Anything that could be optimised: tell the CON and the anesthesia team',
        ] }] },
      ],
      sources: [{ title: 'PACU orientation guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Before you start', blocks: [{ type: 'bullets', items: [
          'PACU chapter in Morgan and Mikhail. Modified Aldrete, postoperative pain and antiemetic drugs, PACU emergencies, ACLS',
        ] }] },
        { title: 'Morning', blocks: [{ type: 'steps', items: [
          '06:30: Anesthesia and Code 1 pagers from the overnight senior. Pick up the PACU phone',
          '06:35: huddle at the whiteboard outside OR 8',
          'Regional team: help with first-case blocks if asked, with the Regional 2 attending',
          'Sign-out on anyone still in the PACU. Check the stat OR setup and that the Pyxis has an airway drug pack',
          'Airway pack from the 4th floor OR pharmacy',
          'Then ICU rounds and add-on consents',
        ] }] },
        { title: 'Afternoon', blocks: [{ type: 'bullets', items: [
          'The on-call senior relieves you at 15:30. You may be asked to help in the OR',
          'Hand over the pagers with a PACU walk-through',
          'Late or Z: you leave the PACU at 15:30 to take a room. Relieve your colleague by 15:30',
          'Wednesday: Grand Rounds first, PACU at 07:30',
          'No scheduled breaks',
        ] }] },
      ],
      sources: [{ title: 'PACU orientation guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },

    epic: {
      title: 'Out-of-OR Intubation',
      sections: [
        { title: 'Open the encounter', blocks: [{ type: 'steps', items: [
          'Find the patient by Patient List (unit and room) or Patient Station (MRN). The OR status board will not show floor patients',
          'Double-click the current admission (highlighted green)',
          'In the procedure box choose Out of OR Intubation',
        ] }] },
        { title: 'Chart, Intraprocedure tab', blocks: [{ type: 'bullets', items: [
          'Anesthesia start: when you reached the bedside',
          'Staff: you and the attending',
          'Intubation note as usual',
          'Medications given',
          'Two sets of vitals around the intubation: Start Out of OR Vitals (then Stop), or enter by hand',
          'Anesthesia stop: when you left',
          'Difficult airway, aspiration, CPR in progress: add a quick note',
          'No preop or postop note',
        ] }, { type: 'note', text: 'Two sets of vitals show the induction did not cause the arrest.' }] },
      ],
      sources: [{ title: 'PACU orientation guide', href: PACU, detail: 'Blackboard, sign-in required' }],
    },
  },
};
